import { useMemo, useState } from "react";
import { feedArticles, catStyle, type Article } from "@/mocks/feed";

const chips = ["全部", "娱乐", "科技", "社会", "财经", "文体"] as const;

interface Props {
  onRead: (a: Article) => void;
}

export default function Feeds({ onRead }: Props) {
  const [cat, setCat] = useState<string>("全部");
  const [kw, setKw] = useState("");

  const list = useMemo(() => {
    let arr = feedArticles;
    if (cat !== "全部") arr = arr.filter((a) => a.category === cat);
    if (kw.trim()) {
      const k = kw.trim().toLowerCase();
      arr = arr.filter((a) => (a.title + a.tag + a.summary + a.category).toLowerCase().includes(k));
    }
    return arr;
  }, [cat, kw]);

  return (
    <section id="feeds" className="w-full scroll-mt-20 bg-background-50">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <div>
              <p className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                <i className="ri-archive-drawer-line" aria-hidden="true"></i> 新鲜速递
              </p>
              <h2 className="font-display mt-3 text-2xl md:text-4xl text-foreground-950">分类速递 · 按口味挑瓜</h2>
              <p className="mt-2 text-sm text-foreground-600">娱乐、科技、社会、财经、文体分类浏览，也可直接搜索关键词。</p>
            </div>

            <div className="relative w-full lg:w-72">
              <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" aria-hidden="true"></i>
              <input
                type="search"
                value={kw}
                onChange={(e) => setKw(e.target.value)}
                placeholder="搜索今日热点…"
                aria-label="搜索热点"
                className="w-full rounded-full border border-background-200 bg-background-100 py-2.5 pl-10 pr-10 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
              />
              {kw && (
                <button
                  type="button"
                  onClick={() => setKw("")}
                  aria-label="清空搜索"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-primary-600 cursor-pointer"
                >
                  <i className="ri-close-circle-fill" aria-hidden="true"></i>
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {chips.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors cursor-pointer ${cat === c ? "bg-foreground-900 text-background-50" : "bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-200 hover:text-primary-700"}`}
              >
                {c === "全部" ? "全部速递" : c}
                {c !== "全部" && (
                  <span className="ml-1.5 text-xs opacity-70">{feedArticles.filter((a) => a.category === c).length}</span>
                )}
              </button>
            ))}
          </div>

          {list.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <i className="ri-inbox-2-line text-4xl text-foreground-300" aria-hidden="true"></i>
              <p className="mt-3 text-sm text-foreground-600">暂时没有找到相关热点，换个关键词试试？</p>
              <button
                type="button"
                onClick={() => { setKw(""); setCat("全部"); }}
                className="mt-4 rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                清除筛选
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {list.map((item, i) => (
                <article
                  key={item.id}
                  className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-100 transition-all hover:-translate-y-1 hover:border-primary-200"
                  onClick={() => onRead(item)}
                >
                  <div className="relative aspect-[16/10] w-full overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                      loading={i > 5 ? "lazy" : undefined}
                    />
                    <span className={`absolute left-3 top-3 rounded-md px-2 py-1 text-xs font-medium ${catStyle[item.category] || "bg-background-200 text-foreground-700"}`}>
                      {item.tag}
                    </span>
                    <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-md bg-black/45 px-2 py-1 text-[11px] text-white backdrop-blur-sm">
                      <i className="ri-eye-line" aria-hidden="true"></i>
                      {item.views}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-4 md:p-5">
                    <div className="flex items-center gap-2 text-xs text-foreground-500">
                      <span className="text-primary-600 font-medium">{item.category}</span>
                      <span className="text-background-300">•</span>
                      <span>{item.time}</span>
                    </div>
                    <h3 className="mt-2 line-clamp-2 text-[15px] font-medium leading-snug text-foreground-900 group-hover:text-primary-700 transition-colors">
                      {item.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
                    <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1"><i className="ri-article-line" aria-hidden="true"></i>{item.source}</span>
                      <span className="flex items-center gap-1 text-primary-700 opacity-0 transition-opacity group-hover:opacity-100">
                        阅读全文 <i className="ri-arrow-right-line" aria-hidden="true"></i>
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}